import styles from '../agents.module.css';

import clsxm from '@/lib/clsxm';

import { brandSafe, OurMark } from './kit';
import type { AlternativePageProps } from './types';
import {
  ArrowGlyph,
  CheckGlyph,
  delay,
  SectionHeading,
  tone,
} from '../primitives';
import { useReveal } from '../useReveal';

/** The short answer: who should switch and who should stay. */
export default function VerdictCards({
  name,
  verdict,
}: Pick<AlternativePageProps, 'name' | 'verdict'>) {
  const { ref, state } = useReveal<HTMLDivElement>(0.2);

  return (
    <section id='verdict' className='py-20 sm:py-24'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <SectionHeading
          title='The short version.'
          tail={brandSafe(
            verdict.tail ?? 'Both are good tools. They suit different teams.',
          )}
        />

        <div
          ref={ref}
          data-reveal={state}
          className={clsxm(styles.reveal, 'mt-12 grid gap-5 lg:grid-cols-2')}
        >
          <div
            className={clsxm(
              styles.rv,
              'relative rounded-[28px] p-7 ring-2 ring-primary-500/60 sm:p-9 dark:ring-primary-400/50',
              tone.surface,
            )}
            style={delay(0)}
          >
            <p className='flex items-center gap-3'>
              <OurMark />
              <span className={clsxm('text-lg font-semibold', tone.ink)}>
                Choose Easy-Peasy.AI if
              </span>
            </p>
            <ul className='mt-6 space-y-4'>
              {verdict.switchIf.map((point) => (
                <li key={point} className='flex gap-3'>
                  <span className='mt-1 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-primary-600 text-white'>
                    <CheckGlyph className='h-3 w-3' />
                  </span>
                  <span className={clsxm('text-[16px] leading-7', tone.body)}>
                    {point}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div
            className={clsxm(
              styles.rv,
              'rounded-[28px] p-7 sm:p-9',
              tone.surface,
              tone.line,
            )}
            style={delay(120)}
          >
            <p className='flex items-center gap-3'>
              <span
                aria-hidden='true'
                className='flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-[#f1f1f5] text-[#56566d] dark:bg-white/[0.07] dark:text-[#a4a4bd]'
              >
                <ArrowGlyph className='h-4 w-4' />
              </span>
              <span className={clsxm('text-lg font-semibold', tone.ink)}>
                {verdict.stayTitle ?? `Stay with ${name} if`}
              </span>
            </p>
            <ul className='mt-6 space-y-4'>
              {verdict.stayIf.map((point) => (
                <li key={point} className='flex gap-3'>
                  <span
                    aria-hidden='true'
                    className='mt-[11px] h-1.5 w-1.5 flex-none rounded-full bg-[#8a8aa1] dark:bg-[#6e6e8c]'
                  />
                  <span className={clsxm('text-[16px] leading-7', tone.body)}>
                    {point}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
